import type { ContextType, ReactElement } from "react";
import { PureComponent } from "react";

import * as _ from "lodash";

import { colorsSets } from "@similarweb/styles";
import {
    Checkbox,
    Dropdown,
    DropdownButton,
    SimpleDropdownItem,
    PlainTooltip,
    Switcher,
    TabSwitchItem,
} from "@similarweb/ui-components";

import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import type { ISegmentsData } from "app/services/conversion/ConversionSegmentsService";

import { ConversionSegmentsUtils } from "../../../../../app/pages/conversion/ConversionSegmentsUtils";
import BoxSubtitle from "../../../../components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "../../../../components/BoxTitle/src/BoxTitle";
import Chart from "../../../../components/Chart/src/Chart";
import "../../../../components/Chart/styles/sharedTooltip.scss";
import { GraphLoader } from "../../../../components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { NoData } from "../../../../components/NoData/src/NoData";
import { TrackContext } from "../../../../components/WithTrack/src/context";
import WithTranslation from "../../../../components/WithTranslation/src/WithTranslation";
import StyledBoxSubtitle from "../../../../styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn } from "../../../../styled components/StyledFlex/src/StyledFlex";
import type { IConversionVertical } from "../../constants/conversionVerticals";
import { conversionVerticals } from "../../constants/conversionVerticals";

export type { IConversionVertical } from "../../constants/conversionVerticals";

import { ADDITIONAL_METRICS, INDUSTRY_AVERAGE_KEY } from "./benchmarkOvertimeConsts";
import { getChartConfig, transformData } from "./SitesVsCategorylineChart";
import {
    BoxContainer,
    Bullet,
    ChartContainer,
    ContentContainer,
    DropdownContainer,
    GraphContainer,
    IndustryAverageCheckboxContainer,
    LabelOverflowHidden,
    LegendContainer,
    LegendTitle,
    NoDataContainer,
    RightUtilsContainer,
    SegmentNameLabel,
    SitesChartLoaderContainer,
    StyledHeaderTitle,
    StyledLegendContainer,
    TabsContainer,
    TitleContainer,
    UtilitiesContainer,
} from "./StyledComponents";
import { trafficSources } from "./trafficSources";

export interface IBenchmarkOvertimeProps {
    title: string;
    titleTooltip: string;
    filters: any;
    data: any;
    selectedRows?: any[];
    isLoading?: boolean;
    renderChart?: (selectedTab: FixmeType, filteredChartData: FixmeType, selectedRows: FixmeType) => ReactElement;
    renderLegend?: (selectedChartData: FixmeType) => ReactElement;
    tableExcelLink?: string;
    industryAverageAvailable?: boolean;
    showChannelDropdown?: boolean;
    rowSelectionProp?: string;
    segmentsData: ISegmentsData;
}

interface IBenchmarkOvertimeState {
    selectedTabIndex: number;
    selectedChannel: string | undefined;
    showIndustryAverage: boolean;
}

export class BenchmarkOvertime extends PureComponent<IBenchmarkOvertimeProps, IBenchmarkOvertimeState> {
    static contextType = TrackContext;

    context: ContextType<typeof TrackContext>;

    public defaultChannel = "Total Traffic";

    private translate: FixmeType;

    constructor(props: IBenchmarkOvertimeProps) {
        super(props);
        this.state = {
            selectedTabIndex: 0,
            selectedChannel: undefined,
            showIndustryAverage: false,
        };
        this.getChannelItems = this.getChannelItems.bind(this);
        this.renderLegend = this.renderLegend.bind(this);
        this.onTabClick = this.onTabClick.bind(this);
        this.onIndustryAverageCheckboxClick = this.onIndustryAverageCheckboxClick.bind(this);
    }

    public componentWillUpdate(nextProps: IBenchmarkOvertimeProps) {
        const { data } = nextProps;
        if (!this.state.selectedChannel && data && data.Data) {
            const availableChannels = this.getAvailableChannels(data) || [];
            if (!this.state.selectedChannel || !_.includes(availableChannels, this.state.selectedChannel)) {
                this.setState({
                    selectedChannel: this.defaultChannel,
                });
            }
        }
    }

    public onTabClick = (value: number) => {
        const { data } = this.props;
        const availableChannels = this.getAvailableChannels(data, value);
        this.context?.track(
            "Metric Button",
            "click",
            `${this.translate(this.props.title)}/Change Tab/${Object.keys(conversionVerticals)[value]}`,
        );
        if (!_.includes(availableChannels, this.state.selectedChannel)) {
            this.setState({
                selectedChannel: this.defaultChannel,
                selectedTabIndex: value,
            });
        } else {
            this.setState({
                selectedTabIndex: value,
            });
        }
    };

    public onSelectedChannelClick = (value: FixmeType) => {
        this.context?.track("Drop Down", "click", `${this.translate(this.props.title)}/Channel/${value.id}`);
        this.setState({
            selectedChannel: value.id,
        });
    };

    public onToggle = (isOpen: boolean) => {
        const event = isOpen ? "open" : "close";
        this.context?.track("Drop Down", event, `${this.translate(this.props.title)}/Channel`);
    };

    public getChannelItems = () => {
        const { data } = this.props;
        const availableChannels = this.getAvailableChannels(data);
        return [
            <DropdownButton
                key={"channel-button"}
                width={180}
            >
                {this.state.selectedChannel}
            </DropdownButton>,
            ..._.map(availableChannels, (channel: string) => {
                return (
                    <SimpleDropdownItem
                        key={channel}
                        id={channel}
                    >
                        {channel}
                    </SimpleDropdownItem>
                );
            }),
        ];
    };

    public renderChart(selectedTab: FixmeType, filteredChartData: FixmeType, selectedRows: FixmeType) {
        const { rowSelectionProp, segmentsData } = this.props;
        const transformedData = transformData(
            filteredChartData,
            selectedRows,
            rowSelectionProp ?? "Domain",
            segmentsData,
        );
        const chart = this.props.renderChart ? (
            this.props.renderChart(selectedTab, filteredChartData, selectedRows)
        ) : (
            <ChartContainer>
                <Chart
                    type={"line"}
                    config={getChartConfig({
                        type: "line",
                        height: 310,
                        filter: selectedTab.filter,
                        data: transformedData,
                        shouldConnectNulls: false,
                        yAxisFilter: selectedTab.yAxisFilter,
                    })}
                    data={transformedData}
                />
            </ChartContainer>
        );
        return chart;
    }

    public renderLegend(selectedChartData: FixmeType) {
        const chartLegend = this.props.renderLegend
            ? this.props.renderLegend(selectedChartData)
            : this._renderLegend(selectedChartData);
        return chartLegend;
    }

    public _renderLegend(selectedChartData: FixmeType) {
        const { selectedRows, rowSelectionProp, segmentsData } = this.props;
        const getLegendItem = (domain: string, key: string, color: string, segmentData?: FixmeType) => {
            return (
                <LegendContainer key={key}>
                    <LegendTitle>
                        <Bullet color={color} />
                        <LabelOverflowHidden>{domain}</LabelOverflowHidden>
                    </LegendTitle>
                    {segmentData && segmentData.segmentName ? (
                        <PlainTooltip
                            placement={"bottom"}
                            tooltipContent={`${domain} - ${segmentData.segmentName}`}
                        >
                            <SegmentNameLabel> {segmentData.segmentName} </SegmentNameLabel>
                        </PlainTooltip>
                    ) : null}
                </LegendContainer>
            );
        };
        const legendItems = selectedRows
            ? _.map(selectedRows, (row: any) => {
                  return getLegendItem(
                      row.Domain,
                      // @ts-ignore FIXME: strict migration - Type 'undefined' cannot be used as an index type — rowSelectionProp is optional
                      row[rowSelectionProp],
                      row.selectionColor,
                      ConversionSegmentsUtils.getSegmentById(segmentsData, row.SegmentId),
                  );
              })
            : _.map(Object.keys(selectedChartData), (chartDomain: string, index) => {
                  return getLegendItem(chartDomain, `t${index}`, colorsSets.c.toArray()[index]);
              });

        if (this.state.showIndustryAverage) {
            const metric = ADDITIONAL_METRICS[INDUSTRY_AVERAGE_KEY];

            legendItems.push(getLegendItem(this.translate(metric.title), metric.key, metric.color));
        }
        return <StyledLegendContainer>{legendItems}</StyledLegendContainer>;
    }

    private getConfidenceData(data: any, selectedTabName: string, selectedChannelName: string) {
        const isSupportedTab = selectedTabName === "ConvertedVisits" || selectedTabName === "ConversionRate";
        if (!isSupportedTab) {
            return null;
        }

        const confidenceData = data?.Data.ConversionRateConfidenceLevel;

        return confidenceData ? confidenceData[selectedChannelName] : null;
    }

    /**
     * Process group data for the chart, and augments it with confidence data, if such exists
     * NOTE: data types are "any" since the data returned from the API makes it impossible
     * for adding a static typing :(
     */
    private processGroupGraphData = (selectedChartData: any, confidenceData: any) => {
        const { selectedRows, rowSelectionProp } = this.props;
        const { showIndustryAverage } = this.state;

        const groupData = Object.keys(selectedChartData)
            .filter((chartDomain) =>
                _.find(selectedRows, (row) => {
                    const includeIndustryAverage = showIndustryAverage && chartDomain === INDUSTRY_AVERAGE_KEY;
                    // @ts-ignore FIXME: strict migration - Type 'undefined' cannot be used as an index type — rowSelectionProp is optional
                    return row[rowSelectionProp] === chartDomain || includeIndustryAverage;
                }),
            )
            .reduce((acc: Record<string, FixmeType>, groupingName) => {
                acc[groupingName] = {
                    data: selectedChartData[groupingName],
                    confidence: confidenceData[groupingName],
                };
                return acc;
            }, {});

        return groupData;
    };

    /**
     * Augments a single website data for the chart with confidence data.
     * NOTE: data types are "any" since the data returned from the API makes it impossible
     * for adding a static typing :(
     */
    private processWebsiteGraphData = (selectedChartData: any[], confidenceData: any) => {
        selectedChartData?.forEach((recordPair: FixmeType) => {
            recordPair.Values?.forEach((record: FixmeType) => {
                const recordDate = record.Key;
                const confidenceAtDate = confidenceData[recordDate];
                record.Confidence = confidenceAtDate;
            });
        });

        return selectedChartData;
    };

    public render() {
        const {
            title,
            titleTooltip,
            filters,
            data,
            selectedRows,
            isLoading,
            industryAverageAvailable,
            showChannelDropdown = true,
        } = this.props;
        const { selectedTabIndex, showIndustryAverage, selectedChannel } = this.state;
        const subtitleFilters = [
            {
                filter: "date",
                value: {
                    from: filters.from,
                    to: filters.to,
                },
            },
            {
                filter: "webSource",
                value: "Desktop", // values: available: 'Total' / 'Desktop'
            },
        ];
        const selectedTab = Object.values(conversionVerticals)[selectedTabIndex];

        const selectedChartData =
            data && data.Data && selectedChannel ? data.Data[selectedTab.dataKey][selectedChannel] : undefined;

        const confidenceData = this.getConfidenceData(data, selectedTab.dataKey, selectedChannel ?? "") ?? {};

        const filteredChartData =
            selectedChartData && selectedRows
                ? this.processGroupGraphData(selectedChartData, confidenceData)
                : this.processWebsiteGraphData(selectedChartData, confidenceData);

        return (
            <WithTranslation>
                {(translate) => {
                    this.translate = translate;
                    return (
                        <BoxContainer data-automation-sites-vs-category={true}>
                            <TitleContainer>
                                <FlexColumn>
                                    <StyledHeaderTitle>
                                        <BoxTitle tooltip={translate(titleTooltip)}>{translate(title)}</BoxTitle>
                                    </StyledHeaderTitle>
                                    <StyledBoxSubtitle>
                                        <BoxSubtitle filters={subtitleFilters} />
                                    </StyledBoxSubtitle>
                                </FlexColumn>
                                {this.props.tableExcelLink ? (
                                    <ExcelButton
                                        url={this.props.tableExcelLink}
                                        trackName="category sites leaders"
                                    />
                                ) : null}
                            </TitleContainer>
                            <ContentContainer>
                                <TabsContainer>
                                    <Switcher
                                        selectedIndex={selectedTabIndex}
                                        className={"sitesVsCategory"}
                                        onItemClick={this.onTabClick}
                                    >
                                        {Object.values(conversionVerticals).map(
                                            (conversionVertical: IConversionVertical) => {
                                                return (
                                                    <TabSwitchItem key={conversionVertical.dataKey}>
                                                        <PlainTooltip
                                                            placement={"top"}
                                                            tooltipContent={i18nFilter()(
                                                                conversionVertical.tooltipInfo,
                                                            )}
                                                        >
                                                            <span>{translate(conversionVertical.title)}</span>
                                                        </PlainTooltip>
                                                    </TabSwitchItem>
                                                );
                                            },
                                        )}
                                    </Switcher>
                                </TabsContainer>
                                {isLoading ? (
                                    <SitesChartLoaderContainer>
                                        <GraphLoader width={"100%"} />
                                    </SitesChartLoaderContainer>
                                ) : (
                                    <GraphContainer className={"sharedTooltip"}>
                                        {selectedChartData ? (
                                            <>
                                                <UtilitiesContainer>
                                                    {this.renderLegend(selectedChartData)}
                                                    {industryAverageAvailable || showChannelDropdown ? (
                                                        <RightUtilsContainer>
                                                            {industryAverageAvailable ? (
                                                                <PlainTooltip
                                                                    tooltipContent={translate(
                                                                        ADDITIONAL_METRICS[INDUSTRY_AVERAGE_KEY]
                                                                            .tooltip,
                                                                    )}
                                                                >
                                                                    <IndustryAverageCheckboxContainer>
                                                                        <Checkbox
                                                                            label={translate(
                                                                                ADDITIONAL_METRICS[INDUSTRY_AVERAGE_KEY]
                                                                                    .label,
                                                                            )}
                                                                            onClick={
                                                                                this.onIndustryAverageCheckboxClick
                                                                            }
                                                                            selected={showIndustryAverage}
                                                                            className="industry-benchmark"
                                                                        />
                                                                    </IndustryAverageCheckboxContainer>
                                                                </PlainTooltip>
                                                            ) : null}
                                                            {showChannelDropdown ? (
                                                                <DropdownContainer style={{ width: "180px" }}>
                                                                    <Dropdown
                                                                        dropdownPopupPlacement={"bottom-left"}
                                                                        selectedIds={{
                                                                            [selectedChannel ?? ""]: true,
                                                                        }}
                                                                        shouldScrollToSelected={true}
                                                                        onToggle={this.onToggle}
                                                                        onClick={this.onSelectedChannelClick}
                                                                    >
                                                                        {this.getChannelItems()}
                                                                    </Dropdown>
                                                                </DropdownContainer>
                                                            ) : null}
                                                        </RightUtilsContainer>
                                                    ) : null}
                                                </UtilitiesContainer>
                                                {this.renderChart(selectedTab, filteredChartData, selectedRows)}
                                            </>
                                        ) : (
                                            <NoDataContainer>
                                                {" "}
                                                <NoData />{" "}
                                            </NoDataContainer>
                                        )}
                                    </GraphContainer>
                                )}
                            </ContentContainer>
                        </BoxContainer>
                    );
                }}
            </WithTranslation>
        );
    }

    private onIndustryAverageCheckboxClick() {
        this.context?.track(
            "Industry Average Checkbox",
            "click",
            `Performance trends/Benchmark Group/${this.state.showIndustryAverage ? "Hide" : "Check"}`,
        );
        this.setState({ showIndustryAverage: !this.state.showIndustryAverage });
    }

    private getAvailableChannels(data: any, selectedTab?: number) {
        return (
            data &&
            Object.keys(
                data.Data[Object.values(conversionVerticals)[selectedTab || this.state.selectedTabIndex].dataKey],
                // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type — trafficSources keys are dynamic
            ).sort((a, b) => (trafficSources[a]?.priority > trafficSources[b]?.priority ? 1 : -1))
        );
    }
}
